import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
    const [cats, setCats] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [name, setName] = useState("");
    const [department, setDepartment] = useState("");
    const [order, setOrder] = useState("0");
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editDep, setEditDep] = useState("");
    const [editOrder, setEditOrder] = useState("0");
    const [filterDept, setFilterDept] = useState("all");

    const filteredCats = useMemo(
        () => (filterDept === "all" ? cats : cats.filter((c) => c.department === filterDept)),
        [cats, filterDept]
    );

    const load = async () => {
        setLoading(true);
        try {
            const [c, d] = await Promise.all([api.get("/categories"), api.get("/departments")]);
            setCats(c.data);
            setDepartments(d.data);
            if (!department && d.data.length) setDepartment(d.data[0].name);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        if (!name.trim() || !department) {
            toast.error("Name and department are required");
            return;
        }
        try {
            await api.post("/categories", {
                name: name.trim(),
                department,
                sort_order: parseInt(order) || 0,
            });
            setName(""); setOrder("0");
            toast.success("Category added");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add");
        }
    };
    const remove = async (c) => {
        if (!window.confirm(`Delete category "${c.name}"?`)) return;
        try {
            await api.delete(`/categories/${c.id}`);
            toast.success("Deleted");
            load();
        } catch { toast.error("Could not delete"); }
    };

    const startEdit = (c) => {
        setEditingId(c.id);
        setEditName(c.name);
        setEditDep(c.department || (departments[0]?.name || ""));
        setEditOrder(String(c.sort_order));
    };
    const cancelEdit = () => setEditingId(null);
    const saveEdit = async (c) => {
        try {
            await api.put(`/categories/${c.id}`, {
                name: editName.trim(),
                department: editDep,
                sort_order: parseInt(editOrder) || 0,
            });
            toast.success("Updated");
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update");
        }
    };

    return (
        <section data-testid="admin-categories-page" className="max-w-2xl">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="font-serif text-3xl">Categories</h2>
                {departments.length > 0 && (
                    <div className="flex items-center gap-2">
                        <div className="w-44">
                            <Select value={filterDept} onValueChange={setFilterDept}>
                                <SelectTrigger data-testid="categories-filter-dept" className="rounded-full h-9">
                                    <SelectValue placeholder="Department" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All departments</SelectItem>
                                    {departments.map((d) => (
                                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {filterDept !== "all" && (
                            <button
                                type="button"
                                data-testid="categories-filter-clear"
                                onClick={() => setFilterDept("all")}
                                className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground px-2 py-1"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
            </div>

            {departments.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground border border-dashed border-border/60 rounded-2xl">
                    Create at least one Department before adding categories.
                </div>
            ) : (
                <div className="bg-card border border-border/60 rounded-2xl p-5 mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Department</Label>
                            <Select value={department} onValueChange={setDepartment}>
                                <SelectTrigger data-testid="new-category-dept" className="mt-1 rounded-xl">
                                    <SelectValue placeholder="Choose department" />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.map((d) => (
                                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Name</Label>
                            <Input
                                data-testid="new-category-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Sandwiches"
                                className="mt-1 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Sort order</Label>
                            <Input
                                data-testid="new-category-order"
                                type="number"
                                value={order}
                                onChange={(e) => setOrder(e.target.value)}
                                className="mt-1 rounded-xl"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button
                                data-testid="add-category-btn"
                                onClick={add}
                                className="rounded-full bg-primary hover:bg-primary-hover h-10 w-full"
                            >
                                <Plus className="h-4 w-4 mr-1" /> Add category
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : cats.length === 0 ? (
                <div data-testid="admin-categories-empty" className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl">
                    No categories yet.
                </div>
            ) : filteredCats.length === 0 ? (
                <div data-testid="admin-categories-no-match" className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl">
                    No categories in this department.
                </div>
            ) : (
                <ul className="space-y-2">
                    {filteredCats.map((c) => (
                        <li
                            key={c.id}
                            data-testid={`category-row-${c.id}`}
                            className="flex items-center justify-between gap-3 bg-card border border-border/60 rounded-2xl px-5 py-3"
                        >
                            {editingId === c.id ? (
                                <>
                                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_1fr_80px] gap-2">
                                        <Input
                                            data-testid={`edit-category-name-${c.id}`}
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            className="rounded-xl h-9"
                                        />
                                        <Select value={editDep} onValueChange={setEditDep}>
                                            <SelectTrigger data-testid={`edit-category-dept-${c.id}`} className="rounded-xl h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {departments.map((d) => (
                                                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Input
                                            data-testid={`edit-category-order-${c.id}`}
                                            type="number"
                                            value={editOrder}
                                            onChange={(e) => setEditOrder(e.target.value)}
                                            className="rounded-xl h-9"
                                        />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => saveEdit(c)} data-testid={`save-category-${c.id}`} className="rounded-full">
                                            <Check className="h-4 w-4 text-moss" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="rounded-full">
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <p className="font-serif text-lg">{c.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {c.department || "—"} · sort {c.sort_order}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => startEdit(c)} data-testid={`edit-category-${c.id}`} className="rounded-full">
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => remove(c)} data-testid={`delete-category-${c.id}`} className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10">
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
